import ImageCarousel from '@/components/ImageCarousel';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Loader2,
  Heart,
  MessageSquare,
  Repeat2,
  Eye,
  Share2,
  Send,
  Shield,
  ShieldCheck,
  Award,
  Flame,
  Calendar,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import ShareModal from './ShareModal';
import Link from '@/components/LocaleLink';
import { useTranslation } from 'react-i18next';
import VerifiedBadge from '@/components/badges/VerifiedBadge';
import ThreadedComments from './ThreadedComments';
import { trackedUrl } from '@/utils/trackedUrl';
import { safeImageSrc } from '@/lib/url';
import { decodeEntities } from '@/utils/decodeEntities';
import { formatDate as formatDateDMY } from '@/lib/dates';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface Scoop {
  metadata?: {media_types?: Record<string,string>};
  id: string;
  title: string;
  description: string | null;
  url: string | null;
  image_url: string | null;
  media_urls: string[] | null;
  tags: string[] | null;
  content_type: string | null;
  like_count: number;
  reply_count: number;
  reshare_count: number;
  view_count: number;
  created_at: string;
  user_id: string;
}

interface Certificate {
  content_hash: string;
  published_at: string;
  signature: string;
  is_original: boolean;
  first_badge: boolean;
  similar_scoop_id: string | null;
}

interface ScoopScore {
  score: number;
  tier: string;
  original_count: number;
  first_count: number;
  streak_days: number;
}

interface Author {
  id: string;
  display_name: string;
  username: string;
  avatar_url: string | null;
  bio: string | null;
  follower_count: number;
  scoop_score: ScoopScore;
  is_verified?: boolean;
  account_type?: string;
}

interface Reply {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  display_name: string;
  username: string;
  avatar_url: string | null;
  is_answer: boolean;
  author_is_verified?: boolean;
}

interface ScoopData {
  scoop: Scoop;
  certificate: Certificate | null;
  author: Author;
  share: { title: string; description: string; image: string; url: string };
}

interface ScoopPanelProps {
  scoopId: string;
  onClose: () => void;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const getAuthToken = () => {
  if (typeof window !== 'undefined') return localStorage.getItem('authToken');
  return null;
};

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatDate(dateStr: string): string {
  return formatDateDMY(dateStr);
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
}

const TIER_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  bronze: { label: 'Bronze', color: 'text-amber-600', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  silver: { label: 'Silver', color: 'text-gray-300', bg: 'bg-gray-400/10', border: 'border-gray-400/20' },
  gold: { label: 'Gold', color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20' },
  platinum: { label: 'Platinum', color: 'text-cyan-300', bg: 'bg-cyan-400/10', border: 'border-cyan-400/20' },
  diamond: { label: 'Diamond', color: 'text-iris-300', bg: 'bg-iris-400/10', border: 'border-iris-400/20' },
};

function getTierConfig(tier: string) {
  return TIER_CONFIG[tier?.toLowerCase()] || TIER_CONFIG.bronze;
}

function stringToColor(str: string | null | undefined): string {
  const s = str || 'default';
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 50%, 35%)`;
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export default function ScoopPanel({ scoopId, onClose }: ScoopPanelProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<ScoopData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Engagement state
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [reshared, setReshared] = useState(false);
  const [reshareCount, setReshareCount] = useState(0);
  const [replyCount, setReplyCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [engagementLoading, setEngagementLoading] = useState<'like' | 'reshare' | 'reply' | null>(null);

  // Replies state
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [replyText, setReplyText] = useState('');

  // Author follow state
  const [isFollowingAuthor, setIsFollowingAuthor] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Fetch scoop data
  useEffect(() => {
    if (!scoopId) return;

    const fetchScoop = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/s/${scoopId}`);
        if (res.status === 404) {
          setError(t('scoops.scoopPanel.postNotFound'));
          return;
        }
        if (!res.ok) throw new Error(t('scoops.scoopPanel.failedToLoadPost'));
        const json: ScoopData = await res.json();
        setData(json);
        setLikeCount(json.scoop.like_count);
        setReshareCount(json.scoop.reshare_count);
        setReplyCount(json.scoop.reply_count);
        setViewCount(json.scoop.view_count + 1);
      } catch {
        setError(t('scoops.scoopPanel.failedToLoadPost'));
      } finally {
        setLoading(false);
      }
    };

    fetchScoop();
  }, [scoopId]);

  // Fetch replies
  const fetchReplies = useCallback(async () => {
    if (!scoopId) return;
    setLoadingReplies(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/scoops/${scoopId}/replies?sort=best&limit=200`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const json = await res.json();
        setReplies(json.replies || []);
      }
    } catch {
      // Ignore errors
    } finally {
      setLoadingReplies(false);
    }
  }, [scoopId]);

  useEffect(() => {
    if (data) {
      fetchReplies();
    }
  }, [data, fetchReplies]);

  // Check if following author
  useEffect(() => {
    if (!data) return;
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/social/is-following/${data.author.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.isFollowing !== undefined) setIsFollowingAuthor(d.isFollowing);
      })
      .catch(() => {});
  }, [data]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle like
  const handleLike = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      showToast(t('scoops.scoopPanel.signInToLike'));
      return;
    }
    if (engagementLoading) return;
    setEngagementLoading('like');
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setLiked(json.liked);
        setLikeCount(json.likeCount ?? (liked ? likeCount - 1 : likeCount + 1));
      } else {
        showToast(t('scoops.scoopPanel.couldNotLike'));
      }
    } catch {
      showToast(t('scoops.scoopPanel.couldNotLike'));
    } finally {
      setEngagementLoading(null);
    }
  }, [scoopId, showToast, engagementLoading, liked, likeCount]);

  // Handle reshare
  const handleReshare = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      showToast(t('scoops.scoopPanel.signInToReshare'));
      return;
    }
    if (engagementLoading || reshared) {
      if (reshared) showToast(t('scoops.scoopPanel.alreadyReshared'));
      return;
    }
    setEngagementLoading('reshare');
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/reshare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setReshared(true);
        setReshareCount((c) => c + 1);
        showToast(t('scoops.scoopPanel.resharedToProfile'));
      } else {
        const json = await res.json().catch(() => ({}));
        showToast(json.error || t('scoops.scoopPanel.couldNotReshare'));
      }
    } catch {
      showToast(t('scoops.scoopPanel.couldNotReshare'));
    } finally {
      setEngagementLoading(null);
    }
  }, [scoopId, showToast, engagementLoading, reshared]);

  // Handle reply submit
  const handleReplySubmit = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      showToast(t('scoops.scoopPanel.signInToReply'));
      return;
    }
    if (!replyText.trim() || engagementLoading) return;
    setEngagementLoading('reply');
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: replyText.trim() }),
      });
      if (res.ok) {
        setReplyCount((c) => c + 1);
        setReplyText('');
        showToast(t('scoops.scoopPanel.replyPosted'));
        // Refetch instead of splicing in the POST response. That response is a
        // { success, reply } envelope, and even unwrapped, `reply` comes from
        // INSERT ... RETURNING * — it carries no author_name/author_username/
        // author_avatar/vote columns. Rendering it directly is what produced
        // "User" / "Invalid Date" / an empty comment body.
        await fetchReplies();
      } else {
        const json = await res.json().catch(() => ({}));
        showToast(json.error || t('scoops.scoopPanel.couldNotPostReply'));
      }
    } catch {
      showToast(t('scoops.scoopPanel.couldNotPostReply'));
    } finally {
      setEngagementLoading(null);
    }
  }, [scoopId, showToast, replyText, engagementLoading, fetchReplies]);

  // Handle follow author
  const handleFollowAuthor = useCallback(async () => {
    if (!data) return;
    const token = getAuthToken();
    if (!token) {
      showToast(t('scoops.scoopPanel.signInToFollow'));
      return;
    }
    setFollowLoading(true);
    const newFollow = !isFollowingAuthor;
    setIsFollowingAuthor(newFollow);
    try {
      await fetch(`${API_URL}/social/follow/${data.author.id}`, {
        method: newFollow ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setIsFollowingAuthor(!newFollow);
    } finally {
      setFollowLoading(false);
    }
  }, [data, isFollowingAuthor, showToast]);

  // Loading state
  if (loading) {
    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40" />
        {/* Panel */}
        <div
          ref={panelRef}
          className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-[#0a0a0f] border-l border-white/5 z-50 flex items-center justify-center"
        >
          <Loader2 className="w-8 h-8 text-iris-500 animate-spin" />
        </div>
      </>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40" onClick={onClose} />
        {/* Panel */}
        <div
          ref={panelRef}
          className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-[#0a0a0f] border-l border-white/5 z-50 flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-wrap gap-y-2">
            <h2 className="text-sm font-semibold text-white">{t('scoops.scoopPanel.post')}</h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/6 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          {/* Error content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-6">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-base font-medium text-white mb-2">{error || t('scoops.scoopPanel.somethingWentWrong')}</h3>
              <p className="text-sm text-[#9a9aac]">{t('scoops.scoopPanel.thePostMayHave')}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const { scoop, certificate, author } = data;
  const tierCfg = getTierConfig(author.scoop_score?.tier || 'bronze');
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/s/${scoopId}` : `/s/${scoopId}`;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40" />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-[#0a0a0f] border-l border-white/5 z-50 flex flex-col animate-in slide-in-from-right duration-300"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0 flex-wrap gap-y-2">
          <h2 className="text-sm font-semibold text-white">{t('scoops.scoopPanel.post1')}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/6 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-5">
            {/* Author header */}
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <Link to={`/u/${author.username}`} className="shrink-0">
                {author.avatar_url ? (
                  <img
                    src={author.avatar_url}
                    alt={author.display_name || author.username}
                    className="w-11 h-11 rounded-full object-cover border-2 border-white/10 hover:border-iris-500/50 transition-colors"
                  />
                ) : (
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-medium border-2 border-white/10 hover:border-iris-500/50 transition-colors"
                    style={{ backgroundColor: stringToColor(author.display_name || author.username) }}
                  >
                    {(author.display_name || author.username || '?')[0].toUpperCase()}
                  </div>
                )}
              </Link>

              {/* Name, username, time */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/u/${author.username}`}
                    className="inline-flex items-center min-h-[24px] text-sm font-medium text-white hover:text-iris-400 transition-colors truncate"
                  >
                    {author.display_name || author.username}
                  </Link>
                  {author.is_verified && <VerifiedBadge className="w-4 h-4" />}
                  {/* Tier badge */}
                  {author.scoop_score?.tier && (
                    <span
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[12px] font-medium ${tierCfg.color} ${tierCfg.bg} border ${tierCfg.border}`}
                    >
                      <Award size={10} />
                      {tierCfg.label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#9a9aac]">
                  <span>@{author.username}</span>
                  <span className="inline-block w-1 h-1 rounded-full bg-[#4e4e60]" />
                  <span>{timeAgo(scoop.created_at)}</span>
                </div>
              </div>

              {/* Follow button */}
              <button
                onClick={handleFollowAuthor}
                disabled={followLoading}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isFollowingAuthor
                    ? 'text-[#9a9aac] bg-white/4 hover:text-red-400 hover:bg-red-500/5'
                    : 'text-[#0C0C12] bg-iris-600 hover:bg-iris-500'
                }`}
              >
                {followLoading ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : isFollowingAuthor ? (
                  t('scoops.scoopPanel.following')
                ) : (
                  t('scoops.scoopPanel.follow')
                )}
              </button>
            </div>

            {/* Title & Description */}
            <div>
              <h1 dir="auto" className="text-lg font-semibold text-white leading-snug mb-2">{decodeEntities(scoop.title)}</h1>
              {scoop.description && (
                <p dir="auto" className="text-sm text-[#a0a0b0] leading-relaxed whitespace-pre-wrap">{decodeEntities(scoop.description)}</p>
              )}
            </div>

            {/* Media */}
            {scoop.image_url && !scoop.media_urls?.length && (
              <div className="rounded-xl overflow-hidden border border-white/5">
                <img
                  src={safeImageSrc(scoop.image_url)}
                  alt={scoop.title}
                  className="w-full object-cover max-h-[300px]"
                  loading="lazy"
                />
              </div>
            )}

        {scoop.media_urls && scoop.media_urls.length > 0 && <div className="mb-4"><ImageCarousel images={scoop.media_urls} mediaTypes={scoop.metadata?.media_types} defaultType={scoop.content_type}/></div>}

            {/* Tags */}
            {scoop.tags && scoop.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {scoop.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 rounded-full text-[13px] text-iris-300 bg-iris-500/10 border border-iris-500/20"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Certificate badge */}
            {certificate && (
              <div className="p-3 rounded-xl bg-white/2 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  {certificate.is_original ? (
                    <ShieldCheck size={16} className="text-emerald-400" />
                  ) : (
                    <Shield size={16} className="text-[#9a9aac]" />
                  )}
                  <span className="text-xs font-medium text-white">
                    {certificate.is_original ? t('scoops.scoopPanel.originalContent') : t('scoops.scoopPanel.verified')}
                  </span>
                  {certificate.first_badge && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[12px] text-amber-400 bg-amber-500/10 border border-amber-500/20">
                      <Flame size={10} />
                      {t('scoops.scoopPanel.first')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[13px] text-[#9a9aac]">
                  <Calendar size={11} />
                  <span>{t('scoops.scoopPanel.published')} {formatDate(certificate.published_at)}</span>
                </div>
              </div>
            )}

            {/* Engagement bar */}
            <div className="flex items-center gap-1 py-3 border-t border-b border-white/5">
              {/* Like */}
              <button
                onClick={handleLike}
                disabled={engagementLoading === 'like'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  liked
                    ? 'text-rose-400 bg-rose-500/10 hover:bg-rose-500/15'
                    : 'text-[#9a9aac] hover:text-rose-400 hover:bg-white/4'
                }`}
              >
                {engagementLoading === 'like' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Heart size={14} fill={liked ? 'currentColor' : 'none'} />
                )}
                {likeCount > 0 && <span>{formatCount(likeCount)}</span>}
              </button>

              {/* Reply */}
              <button
                onClick={() => setShowReplies(!showReplies)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  showReplies
                    ? 'text-iris-400 bg-iris-500/10'
                    : 'text-[#9a9aac] hover:text-iris-400 hover:bg-white/4'
                }`}
              >
                <MessageSquare size={14} />
                {replyCount > 0 && <span>{formatCount(replyCount)}</span>}
              </button>

              {/* Reshare */}
              <button
                onClick={handleReshare}
                disabled={engagementLoading === 'reshare' || reshared}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  reshared
                    ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-[#9a9aac] hover:text-emerald-400 hover:bg-white/4'
                }`}
              >
                {engagementLoading === 'reshare' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Repeat2 size={14} />
                )}
                {reshareCount > 0 && <span>{formatCount(reshareCount)}</span>}
              </button>

              {/* View count */}
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#9a9aac]">
                <Eye size={14} />
                {formatCount(viewCount)}
              </span>

              {/* Share */}
              <button
                onClick={() => setShareModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#9a9aac] hover:text-sky-400 hover:bg-white/4 transition-all ml-auto"
              >
                <Share2 size={14} />
              </button>
            </div>

            {/* Reply form */}
            <div className="rounded-xl border border-white/6 focus-within:border-iris-500/30 focus-within:ring-1 focus-within:ring-iris-500/10 transition-all">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={t('scoops.scoopPanel.writeAReply')}
                rows={2}
                className="w-full bg-transparent text-sm text-white placeholder:text-[#828294] outline-hidden resize-none px-3 pt-3 pb-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleReplySubmit();
                  }
                }}
              />
              <div className="flex items-center justify-end px-2 py-1.5 border-t border-white/4">
                <button
                  onClick={handleReplySubmit}
                  disabled={!replyText.trim() || engagementLoading === 'reply'}
                  className="p-1.5 rounded-lg bg-iris-600 text-[#0C0C12] hover:bg-iris-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {engagementLoading === 'reply' ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                </button>
              </div>
            </div>

            {/* Replies section */}
            {showReplies && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <MessageSquare size={14} className="text-iris-400" />
                    {t('scoops.scoopPanel.replies')}
                    {replyCount > 0 && (
                      <span className="text-xs text-[#9a9aac] font-normal">({replyCount})</span>
                    )}
                  </h3>
                  <button
                    onClick={() => setShowReplies(false)}
                    className="text-[12px] text-[#9a9aac] hover:text-white transition-colors"
                  >
                    {t('scoops.scoopPanel.hide')}
                  </button>
                </div>

                {loadingReplies ? (
                  <div className="flex justify-center py-4">
                    <Loader2 size={16} className="text-iris-400 animate-spin" />
                  </div>
                ) : replies.length === 0 ? (
                  <p className="text-xs text-[#9a9aac] text-center py-4">
                    {t('scoops.scoopPanel.noRepliesYet')}
                  </p>
                ) : (
                  <ThreadedComments
                    scoopId={scoopId}
                    replies={replies}
                    onReplyAdded={fetchReplies}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Share buttons footer */}
        <div className="px-5 py-4 border-t border-white/5 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareModalOpen(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/4 border border-white/6 text-[#a0a0b0] hover:text-white text-xs font-medium transition-colors"
            >
              <Share2 size={14} />
              {t('scoops.scoopPanel.sharePost')}
            </button>
            {scoop.url && (
              <a
                href={trackedUrl(scoop.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-xs font-medium transition-colors"
              >
                <ExternalLink size={14} />
                {t('scoops.scoopPanel.openLink')}
              </a>
            )}
          </div>
        </div>

        {/* Share modal */}
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          url={shareUrl}
          title={scoop.title}
          description={scoop.description || ''}
          coverImage={scoop.image_url}
          tags={scoop.tags || []}
          type="scoop"
        />

        {/* Toast notification */}
        {toast && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-[#1a1a24] border border-white/10 shadow-xl">
            <p className="text-xs text-white">{toast}</p>
          </div>
        )}
      </div>
    </>
  );
}
